/**
 * SECURITY: Authentication input validation utilities
 * Provides client-side validation for credentials before auth calls
 * Prevents weak passwords and malformed inputs from reaching backend
 */

/**
 * SECURITY: Email validation regex
 * Standard RFC 5322 simplified pattern
 * Prevents obvious invalid formats
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Password strength requirements
 * SECURITY: Enforces strong password policy client-side
 * Reduces likelihood of credential compromise
 */
const PASSWORD_REQUIREMENTS = Object.freeze({
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
  allowedSymbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
});

/**
 * SECURITY: Validates email format
 * @param {string} email - Email address to validate
 * @returns {object} { isValid: boolean, error: string|null }
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  // SECURITY: Trim whitespace to prevent bypasses
  const trimmedEmail = email.trim();

  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // SECURITY: Check for suspicious patterns
  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email too long' };
  }

  return { isValid: true, error: null };
};

/**
 * SECURITY: Validates password strength
 * Enforces multiple criteria to prevent weak passwords
 * @param {string} password - Password to validate
 * @returns {object} { 
 *   isValid: boolean, 
 *   errors: string[], 
 *   strength: number (1-5)
 * }
 */
export const validatePasswordStrength = (password) => {
  const errors = [];

  if (!password) {
    return {
      isValid: false,
      errors: ['Password is required'],
      strength: 0,
    };
  }

  // SECURITY: Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(
      `Minimum ${PASSWORD_REQUIREMENTS.minLength} characters required`
    );
  }

  // SECURITY: Require uppercase letter (prevents dictionary attacks)
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Must contain at least one uppercase letter (A-Z)');
  }

  // SECURITY: Require lowercase letter
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Must contain at least one lowercase letter (a-z)');
  }

  // SECURITY: Require number (increases entropy)
  if (PASSWORD_REQUIREMENTS.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('Must contain at least one number (0-9)');
  }

  // SECURITY: Require special character (further increases entropy)
  if (PASSWORD_REQUIREMENTS.requireSymbols) {
    const symbolRegex = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/;
    if (!symbolRegex.test(password)) {
      errors.push(
        `Must contain at least one special character (${PASSWORD_REQUIREMENTS.allowedSymbols})`
      );
    }
  }

  // SECURITY: Detect common weak patterns
  if (/(.)\1{2,}/.test(password)) {
    errors.push('Cannot contain repeated characters (e.g., "aaa")');
  }

  // SECURITY: Calculate strength score (1-5)
  // Higher score = more complex password
  let strength = 1;
  if (password.length >= 12) strength++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) strength++;

  return {
    isValid: errors.length === 0,
    errors,
    strength: Math.min(strength, 5),
  };
};

/**
 * SECURITY: Sanitizes login inputs before authentication
 * Trims whitespace and normalizes email
 * @param {string} email - Email to sanitize
 * @param {string} password - Password (not trimmed - whitespace is intentional)
 * @returns {object} { email: string, password: string }
 */
export const sanitizeLoginInputs = (email, password) => {
  return {
    email: (email || '').trim().toLowerCase(),
    password: password || '', // Don't trim password - intentional spaces matter
  };
};

/**
 * SECURITY: Validates all login inputs at once
 * Combines email and password validation for login flow
 * @param {string} email
 * @param {string} password
 * @returns {object} { isValid: boolean, errors: string[], sanitized: object }
 */
export const validateLoginInputs = (email, password) => {
  const errors = [];

  // Validate email
  const emailValidation = validateEmail(email);
  if (!emailValidation.isValid) {
    errors.push(emailValidation.error);
  }

  // SECURITY: Allow weak passwords on login (user might have old account)
  // but DO validate password is present (prevents empty password attacks)
  if (!password || password.length === 0) {
    errors.push('Password is required');
  }

  const sanitized = sanitizeLoginInputs(email, password);

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
  };
};

/**
 * SECURITY: Validates all registration inputs
 * Enforces both email validity AND password strength
 * @param {string} email
 * @param {string} password
 * @returns {object} { isValid: boolean, errors: string[], sanitized: object }
 */
export const validateRegistrationInputs = (email, password) => {
  const errors = [];

  // Validate email
  const emailValidation = validateEmail(email);
  if (!emailValidation.isValid) {
    errors.push(emailValidation.error);
  }

  // SECURITY: Strictly validate password strength for new accounts
  const passwordValidation = validatePasswordStrength(password);
  if (!passwordValidation.isValid) {
    errors.push(...passwordValidation.errors);
  }

  const sanitized = sanitizeLoginInputs(email, password);

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
    passwordStrength: passwordValidation.strength,
  };
};
